// --
import React from 'react';
import {DropdownItem, DropdownToggle, DropdownMenu, UncontrolledDropdown, UncontrolledNavDropdown} from 'reactstrap';
import classNames from 'classnames';
import Icon from "../icon/FAIcon";
import NavIconText from "../navbar/NavIconText";
import AbstractReactComponent from "../AbstractReactComponent";

import "./BaseAppWrapper.scss";
import AppNavbarWrapper from "./AppNavbarWrapper";

const appThemes = {
    ["default"]: {
        color: "blue",
        className: ""
    },
    ["retro-green"]: {
        color: "black",
        className: "retro-green"
    },
    ["retro-white"]: {
        color: "white",
        className: "retro-white"
    }
};

/**
 * Wrapper pro PAGE stránky aplikace.
 *
 * Použití:
 *
 * 1. Vytvořit novou komponentu AppWrapper
 *
 * class AppWrapper extends AbstractReactComponent {
 *   render() {
 *      const {a, b, c, d, ...other} = this.props;
 *      return <layout.BaseAppWrapper
 *          {...other}
 *          onHistoryGoBack=...
 *          centerAddons=...
 *          onLogout=...
 *          collapseAddons=...
 *          userMenuAddons=...
 *          authStatus=...
 *      />
 *   }
 * }
 *
 * 2. Použít komponentu aplikace
 *        return <AppWrapper appName="Správní řízení" views={modules.getModulePohledViews(MODULE_CODE_SR, null, authInfo)}>
 *            <layout.PageWrapper>
 *                <layout.PageContent
 *                    leftPanel={<OznameniListPanel area={oznameniActions.OZNAMENI_PREHLED_AREA} />}
 *                    rightPanel={<PripominkaListPanel area={pripominkaActions.PRIPOMINKY_PREHLED_AREA} />}
 *                >
 *                    <ReferentPripadyList
 *                        kodTypPripad={kodTypPripad}
 *                        vlastnik={typyPripadu}
 *                    />
 *                </layout.PageContent>
 *            </layout.PageWrapper>
 *        </AppWrapper
 */
class BaseAppWrapper extends AbstractReactComponent {
    static propTypes = {
        appCode: React.PropTypes.string,
        appName: React.PropTypes.oneOfType([    // název aplikace, který se zobrazuje jako brand, mu6e se jednat i o název globální logické sekce, např. skprávní řízení apod.
            React.PropTypes.array,
            React.PropTypes.node
        ]),
        detail: React.PropTypes.oneOfType([ // detail informace aplikace, např. text detailu stránky, globální filtr stránky atp.
            React.PropTypes.array,
            React.PropTypes.node
        ]),
        homeLink: React.PropTypes.oneOfType([
            React.PropTypes.func,   // pokud je uvedeno, volá se tento callback při klinutí
            React.PropTypes.string, // přesouvá se na uvedenou url - url pro router
        ]),
        onHistoryGoBack: React.PropTypes.func.isRequired,   // funkce pro zpracování posunu back, předpis: (goBackBase, homeLink) => {}
        centerAddons: React.PropTypes.oneOfType([   // addony do prostřední oblasti navbaru, typicky fulltext hledání apod.
            React.PropTypes.array,
            React.PropTypes.node
        ]),
        onLogout: React.PropTypes.func,  // logout funkce
        collapseAddons: React.PropTypes.array,  // pole dalších tlačítek, které se zobrazí vedle tlačítka pohled (nalevo), typicky pole NavItem
        userMenuAddons: React.PropTypes.array,  // pole dalších tlačítek do menu pod uživatelem, typicky pole DropdownItem
        views: React.PropTypes.array,   // seznam objektů { name: "", route: "/xxx" }, které představují jednotlivé pohledy v aplikaci
        currentView: React.PropTypes.string,    // aktuálně přepnutý pohled - route
        backButtonIcon: React.PropTypes.bool,   // má se místo loga aplikace zobrazit back button?
        goBack: React.PropTypes.bool,   // pokud je true, je kliknutí na brand bráno jako back
        goBackBase: React.PropTypes.string, // základní url, která reprezentuje základ aktuálně zobrazované stránky - při stisku back se půjde na první, která namá prefix uvedený v goBackBase
        hasLeftPanel: React.PropTypes.bool, // aplikace má levý panel, jedná se o případný zasouvací panel
        hasRightPanel: React.PropTypes.bool, // aplikace má pravý panel, jedná se o případný zasouvací panel
        leftPanelCollapsed: React.PropTypes.bool,   // pokud je true, je levý panel změnšen a není dokován
        authStatus: React.PropTypes.object,   // informace o přihlášeném uživateli, předpis: {auth: bool, fullname: string, login: string}, pokud je null nebo je auth false, není přihlášen
        testEnvironment: React.PropTypes.bool,     // pokud je true, jedná se o testovací prostředí
    };

    static defaultProps = {
        appName: "Agendio",
        collapseAddons: [],
        userMenuAddons: [],
        backButtonIcon: false,
        views: [],
        currentView: "",
        goBack: false,
        hasLeftPanel: false,
        hasRightPanel: false,
        leftPanelCollapsed: false,
        rightPanelCollapsed: false,
        testEnvironment: false,
    };

    constructor(props) {
        super(props);

        let themeKey = localStorage.getItem("proxio-app-theme") || "default";
        this.state = {
            selectedThemeKey: themeKey
        };
        this.applyTheme(themeKey);
    }

    applyTheme = themeKey => {
        const theme = appThemes[themeKey];
        document.getElementsByTagName("BODY")[0].className = theme.className;
    };

    handleChangeTheme = (themeKey) => {
        localStorage.setItem("proxio-app-theme", themeKey);
        this.setState({selectedThemeKey: themeKey});

        this.applyTheme(themeKey);
    };

    render() {
        const {onHistoryGoBack, appCode, onLogout, userMenuAddons, collapseAddons, centerAddons, authStatus, className, leftPanelCollapsed, rightPanelCollapsed, hasLeftPanel, hasRightPanel, children, goBackBase, appName, detail, homeLink, backButtonIcon, goBack, testEnvironment} = this.props;
        const {selectedThemeKey} = this.state;

        let userNameInfo;
        if (authStatus && authStatus.auth) {
            userNameInfo = authStatus.fullname || authStatus.login;
        }

        const useCollapseAddons = [...collapseAddons];

        let themesAction;
        if (__DEV__) {
            themesAction = <div className="theme-changer">
                {Object.keys(appThemes).map(themeKey => {
                    let theme = appThemes[themeKey];
                    return <span className={themeKey === selectedThemeKey ? "active" : ""}
                                 onClick={() => this.handleChangeTheme(themeKey)}
                                 style={{backgroundColor: theme.color}}>&nbsp;</span>

                })}
            </div>;
        }

        authStatus && authStatus.auth && useCollapseAddons.push(
            <UncontrolledNavDropdown key="user">
                <UncontrolledDropdown id="user">
                    <DropdownToggle nav className="icon-text">
                        <NavIconText icon={<Icon icon='user'/>} text={userNameInfo} />
                    </DropdownToggle>
                    <DropdownMenu right>
                        {userMenuAddons}
                        <DropdownItem onClick={onLogout}>Odhlásit</DropdownItem>
                        {themesAction}
                    </DropdownMenu>
                </UncontrolledDropdown>
            </UncontrolledNavDropdown>
        );

        const wrapperClassNames = classNames(
            className,
            "app-wrapper",
            {
                "has-left-panel": hasLeftPanel,
                "has-right-panel": hasRightPanel,
                "left-panel-collapsed": leftPanelCollapsed,
                "right-panel-collapsed": rightPanelCollapsed,
            }
        );
        return (
            <div key="base-app-wrapper" className={wrapperClassNames}>
                <div key="navbar" className={"app-wrapper-navbar"}>
                    <AppNavbarWrapper
                        key="bar"
                        onHistoryGoBack={onHistoryGoBack}
                        appName={appName}
                        appCode={appCode}
                        detail={detail}
                        homeLink={homeLink}
                        backButtonIcon={backButtonIcon}
                        goBack={goBack}
                        goBackBase={goBackBase}
                        collapseAddons={useCollapseAddons}
                        centerAddons={centerAddons}
                        testEnvironment={testEnvironment}
                    />
                </div>
                <div key="content" className={"app-wrapper-content"}>
                    {children}
                </div>
            </div>
        )
    }
}

export default BaseAppWrapper;
